import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE_URL from './apiConfig';
import AdminLayout from './AdminLayouts';
import { useNavigate } from 'react-router-dom';
import localforage from 'localforage';
import { FiEye, FiEyeOff } from 'react-icons/fi';

const AdminCompany = () => {
  const navigate = useNavigate();
  const [companyData, setCompanyData] = useState({
    companyId: 3,
    companyName: '',
    email: '',
    minimumOrder: '',
    password: '',
    logo: null,
    logoUrl: ''
  });
  
  const [logoPreview, setLogoPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = await localforage.getItem('jwtToken');
        if (!token) {
          alert('⚠️ Please login to access this page');
          navigate('/AdminLogin');
          return;
        }
        // If authenticated, fetch company data
        fetchCompanyData();
      } catch (error) {
        console.error('Auth check error:', error);
        navigate('/AdminLogin');
      }
    };
    checkAuth();
  }, [navigate]);

  const fetchCompanyData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}api/Crackers/GetMinimumOrderAmount?companyId=3`);
      if (response.data.statusCode === 200 && response.data.data) {
        const data = response.data.data;
        
        // Handle logo URL - prepend base URL if it's a relative path
        let logoUrl = data.companyLogo || '';
        if (logoUrl && !logoUrl.startsWith('http')) {
          // Remove leading slash if present
          logoUrl = logoUrl.startsWith('/') ? logoUrl.substring(1) : logoUrl;
          logoUrl = `${API_BASE_URL}${logoUrl}`;
        }
        
        setCompanyData({
          companyId: data.companyId || 3,
          companyName: data.companyName || '',
          email: data.companyEmail || '',
          minimumOrder: data.minOrderAmount || '',
          password: data.userPassword || '', // Display password if API returns it
          logo: null,
          logoUrl: logoUrl
        });
        if (logoUrl) {
          setLogoPreview(logoUrl);
        }
      }
    } catch (error) {
      console.error('Error fetching company data:', error);
      alert('❌ Failed to load company data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoRemove = () => {
    setLogoPreview(null);
    setCompanyData(prev => ({
      ...prev,
      logoUrl: 'REMOVE_LOGO', // Special flag to indicate logo should be removed
      logo: null
    }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    console.log('File selected:', file);
    
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
      if (!validTypes.includes(file.type)) {
        alert('⚠️ Please upload only JPG or PNG images');
        e.target.value = '';
        return;
      }

      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        alert('⚠️ File size should be less than 2MB');
        e.target.value = '';
        return;
      }

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);

      console.log('Setting logo file in state:', file.name);
      setCompanyData(prev => {
        const updated = {
          ...prev,
          logo: file,
          logoUrl: '' // Clear logoUrl when new file is selected
        };
        console.log('Updated companyData:', updated);
        return updated;
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCompanyData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('=== SUBMIT STARTED ===');
    console.log('companyData.logo:', companyData.logo);
    console.log('companyData.logoUrl:', companyData.logoUrl);
    console.log('Full companyData:', companyData);
    
    // Validation
    if (!companyData.minimumOrder) {
      alert('⚠️ Please enter minimum order amount');
      return;
    }

    if (companyData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(companyData.email)) {
      alert('⚠️ Please enter a valid email address');
      return;
    }

    try {
      setLoading(true);

      // Get JWT token from localforage
      const localforage = (await import('localforage')).default;
      const token = await localforage.getItem('jwtToken');

      // Create FormData for the API call
      const formData = new FormData();
      formData.append('CompanyId', companyData.companyId.toString());
      formData.append('MinAmount', companyData.minimumOrder.toString());
      
      if (companyData.email) {
        formData.append('Email', companyData.email);
      }
      
      if (companyData.password) {
        formData.append('Password', companyData.password);
      }
      
      // Handle logo: send file directly or empty string for removal
      console.log('Logo check - logoUrl:', companyData.logoUrl, 'logo file:', companyData.logo);
      
      if (companyData.logo) {
        // User selected a new file - send as LogoFile
        formData.append('LogoFile', companyData.logo);
        console.log('Logo: NEW FILE', companyData.logo.name, companyData.logo.type);
      } else if (companyData.logoUrl === 'REMOVE_LOGO') {
        // User clicked remove - send empty string in CompanyLogo
        formData.append('CompanyLogo', '');
        console.log('Logo: REMOVED (empty string)');
      } else if (companyData.logoUrl) {
        // No change - keep existing logo URL
        formData.append('CompanyLogo', companyData.logoUrl);
        console.log('Logo: EXISTING URL', companyData.logoUrl);
      } else {
        console.log('Logo: NO LOGO DATA');
      }

      console.log('FormData contents:');
      for (let pair of formData.entries()) {
        console.log(pair[0], pair[1]);
      }

      const response = await axios.post(
        `${API_BASE_URL}api/Crackers/UpdateMinimumOrderAmount`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`
          },
        }
      );

      if (response.data.statusCode === 200) {
        alert('✅ ' + (response.data.statusDesc || 'Company settings updated successfully!'));
        // Clear password field after successful update
        setCompanyData(prev => ({ ...prev, password: '', logo: null }));
        fetchCompanyData(); // Refresh data
      } else {
        alert('❌ ' + (response.data.statusDesc || 'Failed to update company settings'));
      }
    } catch (error) {
      console.error('Error updating company:', error);
      if (error.response?.status === 401) {
        alert('❌ Unauthorized. Please login again.');
      } else {
        const errorMsg = error.response?.data?.statusDesc || error.response?.data?.message || 'Failed to update company settings';
        alert('❌ ' + errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <AdminLayout />
      <div style={styles.container}>
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>Company Settings</h1>
            <p style={styles.subtitle}>Manage your company information and settings</p>
          </div>
          <button
            type="submit"
            style={styles.submitButton}
            disabled={loading}
            onClick={handleSubmit}
          >
            {loading ? 'Updating...' : 'Update Settings'}
          </button>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          {/* First Row: 3 Fields */}
          <div style={styles.threeColumnGrid}>
            {/* Company Name - Read Only */}
            <div style={styles.formGroup}>
              <label style={styles.label}>
                Company Name
              </label>
              <input
                type="text"
                name="companyName"
                value={companyData.companyName}
                style={{...styles.input, backgroundColor: '#e9ecef', cursor: 'not-allowed'}}
                readOnly
              />
            </div>

            {/* Email - Editable */}
            <div style={styles.formGroup}>
              <label style={styles.label}>
                Email (Order Details Update)
              </label>
              <input
                type="email"
                name="email"
                value={companyData.email}
                onChange={handleInputChange}
                style={styles.input}
                placeholder="company@example.com"
              />
            </div>

            {/* Minimum Order - Editable */}
            <div style={styles.formGroup}>
              <label style={styles.label}>
                Minimum Order Amount (₹) <span style={styles.required}>*</span>
              </label>
              <input
                type="number"
                name="minimumOrder"
                value={companyData.minimumOrder}
                onChange={handleInputChange}
                style={styles.input}
                placeholder="3000"
                min="0"
                required
              />
            </div>
          </div>

          {/* Second Row: Password and Logo */}
          <div style={styles.twoColumnGrid}>
            {/* Admin Password - Editable */}
            <div style={styles.formGroup}>
              <label style={styles.label}>
                Admin Password (Leave blank to keep current)
              </label>
              <div style={styles.passwordInputContainer}>
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={companyData.password}
                  onChange={handleInputChange}
                  style={styles.passwordInput}
                  placeholder="Enter new admin password"
                  autoComplete="new-password"
                />
                <div 
                  onClick={() => setShowPassword(!showPassword)}
                  style={styles.eyeIcon}
                  title={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </div>
              </div>
            </div>

            {/* Logo Section */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Company Logo</label>
              <div style={styles.logoSection}>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/jpg"
                  onChange={handleLogoChange}
                  style={styles.fileInput}
                />
                {logoPreview && (
                  <div style={styles.logoPreviewContainer}>
                    <div style={styles.logoPreview}>
                      <img src={logoPreview} alt="Company Logo" style={styles.logoImage} />
                      {/* X Icon to Remove */}
                      <div
                        onClick={handleLogoRemove}
                        style={styles.removeIcon}
                        title="Remove logo"
                      >
                        ×
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

const styles = {
  container: {
    maxWidth: '1000px',
    margin: '2rem auto',
    padding: '2rem',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    // width: 'fit-content',
  },
  header: {
    marginBottom: '2rem',
    borderBottom: '2px solid #5e045bff',
    paddingBottom: '1rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '1rem',
  },
  title: {
    fontSize: '2rem',
    color: '#5e045bff',
    margin: '0 0 0.5rem 0',
  },
  subtitle: {
    fontSize: '1rem',
    color: '#666',
    margin: 0,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
  },
  threeColumnGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '1.9rem',
  },
  twoColumnGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '1.9rem',
    alignItems: 'start',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  label: {
    fontSize: '0.85rem',
    fontWeight: '600',
    color: '#333',
  },
  required: {
    color: '#ff0000',
  },
  input: {
    padding: '0.6rem',
    border: '2px solid #5e045bff',
    borderRadius: '6px',
    fontSize: '0.85rem',
    outline: 'none',
    transition: 'border-color 0.3s',
    backgroundColor: '#f3e5f5',
    width: '100%',
  },
  passwordInputContainer: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  passwordInput: {
    padding: '0.6rem',
    paddingRight: '2.5rem',
    border: '2px solid #5e045bff',
    borderRadius: '6px',
    fontSize: '0.85rem',
    outline: 'none',
    transition: 'border-color 0.3s',
    backgroundColor: '#f3e5f5',
    width: '100%',
  },
  eyeIcon: {
    position: 'absolute',
    right: '0.75rem',
    cursor: 'pointer',
    color: '#5e045bff',
    fontSize: '1.2rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'color 0.3s',
    ':hover': {
      color: '#4a0348ff',
    },
  },
  logoSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  logoPreviewContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },
  logoPreview: {
    width: '120px',
    height: '120px',
    border: '2px solid #5e045bff',
    borderRadius: '8px',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f3e5f5',
    position: 'relative',
  },
  logoImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
  removeIcon: {
    position: 'absolute',
    top: '5px',
    right: '5px',
    width: '24px',
    height: '24px',
    backgroundColor: '#dc2626',
    color: 'white',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.5rem',
    fontWeight: 'bold',
    cursor: 'pointer',
    lineHeight: '1',
    transition: 'all 0.2s',
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
  },
  fileInput: {
    padding: '0.5rem',
    border: '2px solid #5e045bff',
    borderRadius: '6px',
    fontSize: '0.85rem',
    cursor: 'pointer',
    width: '100%',
  },
  buttonGroup: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '1rem',
  },
  submitButton: {
    padding: '0.75rem 2rem',
    backgroundColor: '#5e045bff',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '0.95rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s',
    whiteSpace: 'nowrap',
    alignSelf: 'flex-start',
  },
};

export default AdminCompany;
